import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
	Table,
	TableBody,
	TableCell,
  TableContainer,
	TableHead,
	TableRow,
  Paper,
  TablePagination,
  Chip,
  Grid,
  InputAdornment,
  Tooltip,
  IconButton,
	Dialog,
  DialogTitle,
	DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AddDepartment from './AddDepartment';

// Business options
const businessOptions = [
	{ id: 1, name: 'a1 Technologies' },
	{ id: 2, name: 'a1 Retail Solutions' },
	{ id: 3, name: 'a1 Consulting' },
];

const DepartmentSetup = () => {
	// State management
  const [searchQuery, setSearchQuery] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
	const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
	const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

	// Form state
  const [newDepartment, setNewDepartment] = useState({
    businessId: '',
		departmentName: '',
		description: '',
		status: 'active',
	});

  // Mock department data
	const [departments, setDepartments] = useState([
		{
			id: 1,
			businessId: 1,
			departmentName: 'Human Resources',
			description: 'Handles recruitment, payroll, and employee relations.',
			status: 'active',
			createdDate: '2020-01-15',
		},
		{
			id: 2,
			businessId: 2,
			departmentName: 'Finance',
			description: 'Manages company finances and accounts.',
			status: 'active',
			createdDate: '2021-06-20',
		},
		{
			id: 3,
			businessId: 3,
			departmentName: 'IT',
			description: 'Responsible for IT infrastructure and support.',
			status: 'inactive',
			createdDate: '2022-03-10',
		},
	]);

	// Filter departments
  const filteredDepartments = departments.filter((dept) => {
    const matchesSearch = dept.departmentName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || dept.status === statusFilter;
		return matchesSearch && matchesStatus;
	});

  // Pagination
  const paginatedDepartments = filteredDepartments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPage(0);
  };

  const handleOpenModal = () => {
    setNewDepartment({
			businessId: '',
			departmentName: '',
			description: '',
			status: 'active',
		});
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedDepartment(null);
  };

  const handleView = (department) => {
    setSelectedDepartment(department);
    setOpenViewModal(true);
  };

  const handleEdit = (department) => {
    setSelectedDepartment(department);
    setNewDepartment(department);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this department?')) {
		setIsLoading(true);
		try {
			await new Promise(resolve => setTimeout(resolve, 1000));
        setDepartments(departments.filter((dept) => dept.id !== id));
        setSuccessMessage('Department deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
		} catch (error) {
        setErrors({ submit: 'Failed to delete department' });
		} finally {
			setIsLoading(false);
		}
    }
  };

  const handleDepartmentAdded = (newDept) => {
    setDepartments([...departments, newDept]);
    setSuccessMessage('Department added successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleSaveDepartment = async () => {
    const validationErrors = {};
    if (!newDepartment.businessId) validationErrors.businessId = 'Business is required';
    if (!newDepartment.departmentName) validationErrors.departmentName = 'Department name is required';

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
			return;
		}

		setIsLoading(true);
		try {
			await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (selectedDepartment) {
        // Update existing
        setDepartments(departments.map((dept) =>
          dept.id === selectedDepartment.id ? { ...dept, ...newDepartment } : dept
        ));
        setSuccessMessage('Department updated successfully!');
      } else {
        // Add new
        const newDept = {
          ...newDepartment,
          id: Date.now(),
          createdDate: new Date().toISOString().split('T')[0],
        };
        setDepartments([...departments, newDept]);
        setSuccessMessage('Department added successfully!');
      }

      handleCloseModal();
      setTimeout(() => setSuccessMessage(''), 3000);
		} catch (error) {
      setErrors({ submit: 'Failed to save department' });
		} finally {
			setIsLoading(false);
		}
	};

  const getStatusStyle = (status) => {
    return status === 'active'
      ? { backgroundColor: '#e8f5e9', color: '#2e7d32' }
      : { backgroundColor: '#ffebee', color: '#c62828' };
	};

	return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              Department Setup
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage organization departments
            </Typography>
          </Box>
        </Box>
						<AddDepartment onDepartmentAdded={handleDepartmentAdded} />
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
									placeholder="Search departments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
							</Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{ textTransform: 'none' }}
              >
                Search
              </Button>
							<Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleReset}
                sx={{ textTransform: 'none' }}
              >
                Reset
							</Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
							<Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Department Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Business Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedDepartments.length === 0 ? (
									<TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No departments found
                    </Typography>
                  </TableCell>
									</TableRow>
              ) : (
                paginatedDepartments.map((dept) => {
                  const business = businessOptions.find(b => b.id === dept.businessId);
															return (
                    <TableRow key={dept.id} hover>
                      <TableCell>{dept.departmentName}</TableCell>
																	  <TableCell>{business ? business.name : '-'}</TableCell>
                      <TableCell>{dept.description}</TableCell>
																	<TableCell>
                        <Chip
                          label={dept.status}
                          size="small"
                          sx={getStatusStyle(dept.status)}
                        />
																	</TableCell>
																	<TableCell>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => handleView(dept)}>
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(dept)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(dept.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
																	</TableCell>
																</TableRow>
															);
                })
              )}
													</TableBody>
							</Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredDepartments.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          
        />
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 'bold',
          }}
        >
          {selectedDepartment ? 'Edit Department' : 'Add Department'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.submit}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth error={Boolean(errors.businessId)}>
                <InputLabel>Business *</InputLabel>
                <Select
                  value={newDepartment.businessId}
                  onChange={(e) => setNewDepartment({ ...newDepartment, businessId: e.target.value })}
                  label="Business *"
                >
                  {businessOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
									</Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Department Name *"
                value={newDepartment.departmentName}
                onChange={(e) => setNewDepartment({ ...newDepartment, departmentName: e.target.value })}
                error={Boolean(errors.departmentName)}
                helperText={errors.departmentName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={newDepartment.description}
                onChange={(e) => setNewDepartment({ ...newDepartment, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newDepartment.status}
                  onChange={(e) => setNewDepartment({ ...newDepartment, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
				</DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} disabled={isLoading}>
							Cancel
						</Button>
          <Button
            variant="contained"
            onClick={handleSaveDepartment}
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            {isLoading ? 'Saving...' : 'Save'}
						</Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Department Details</DialogTitle>
        <DialogContent>
          {selectedDepartment && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Department Name:</strong> {selectedDepartment.departmentName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Business:</strong>{' '}
                {businessOptions.find(b => b.id === selectedDepartment.businessId)?.name || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Description:</strong> {selectedDepartment.description}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Status:</strong>{' '}
                <Chip label={selectedDepartment.status} size="small" sx={getStatusStyle(selectedDepartment.status)} />
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Created Date:</strong> {selectedDepartment.createdDate}
              </Typography>
            </Box>
          )}
				</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewModal(false)}>Close</Button>
        </DialogActions>
			</Dialog>
    </Box>
	);
};

export default DepartmentSetup;
